import {
  Root,
  Portal,
  Trigger,
  Content,
  Group,
  Item,
  CheckboxItem,
  RadioGroup,
  RadioItem,
  Label,
  Separator,
  Sub,
  SubTrigger,
  SubContent,
  ItemIndicator,
} from '@radix-ui/react-dropdown-menu';
import { CheckIcon, ChevronRightIcon, CircleIcon } from 'lucide-react';
import { type ComponentProps, type ReactElement } from 'react';
import { cn } from '@avanta/shared/utils/cn';
import * as styles from './styles';

const DropdownMenu = ({ ...props }: ComponentProps<typeof Root>): ReactElement => (
  <Root data-slot="dropdown-menu" {...props} />
);

const DropdownMenuPortal = ({ ...props }: ComponentProps<typeof Portal>): ReactElement => (
  <Portal data-slot="dropdown-menu-portal" {...props} />
);

const DropdownMenuTrigger = ({ ...props }: ComponentProps<typeof Trigger>): ReactElement => (
  <Trigger data-slot="dropdown-menu-trigger" {...props} />
);

const DropdownMenuContent = ({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof Content>): ReactElement => (
  <Portal>
    <Content
      data-slot="dropdown-menu-content"
      sideOffset={sideOffset}
      className={cn(styles.DROPDOWN_MENU_CONTENT_STYLES, 'notranslate', className)}
      translate="no"
      {...props}
    />
  </Portal>
);

const DropdownMenuGroup = ({ ...props }: ComponentProps<typeof Group>): ReactElement => (
  <Group data-slot="dropdown-menu-group" {...props} />
);

const DropdownMenuItem = ({
  className,
  inset,
  variant = 'default',
  ...props
}: ComponentProps<typeof Item> & {
  inset?: boolean;
  variant?: 'default' | 'destructive';
}): ReactElement => (
  <Item
    data-slot="dropdown-menu-item"
    data-inset={inset}
    data-variant={variant}
    className={cn(styles.DROPDOWN_MENU_ITEM_STYLES, className)}
    {...props}
  />
);

const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ...props
}: ComponentProps<typeof CheckboxItem>): ReactElement => (
  <CheckboxItem
    data-slot="dropdown-menu-checkbox-item"
    className={cn(styles.DROPDOWN_MENU_CHECKBOX_ITEM_STYLES, className)}
    checked={checked}
    {...props}
  >
    <span className={styles.DROPDOWN_MENU_INDICATOR_SPAN_STYLES}>
      <ItemIndicator>
        <CheckIcon className="size-4" />
      </ItemIndicator>
    </span>
    {children}
  </CheckboxItem>
);

const DropdownMenuRadioGroup = ({ ...props }: ComponentProps<typeof RadioGroup>): ReactElement => (
  <RadioGroup data-slot="dropdown-menu-radio-group" {...props} />
);

const DropdownMenuRadioItem = ({
  className,
  children,
  ...props
}: ComponentProps<typeof RadioItem>): ReactElement => (
  <RadioItem
    data-slot="dropdown-menu-radio-item"
    className={cn(styles.DROPDOWN_MENU_RADIO_ITEM_STYLES, className)}
    {...props}
  >
    <span className={styles.DROPDOWN_MENU_INDICATOR_SPAN_STYLES}>
      <ItemIndicator>
        <CircleIcon className="size-2 fill-current" />
      </ItemIndicator>
    </span>
    {children}
  </RadioItem>
);

const DropdownMenuLabel = ({
  className,
  inset,
  ...props
}: ComponentProps<typeof Label> & {
  inset?: boolean;
}): ReactElement => (
  <Label
    data-slot="dropdown-menu-label"
    data-inset={inset}
    className={cn(styles.DROPDOWN_MENU_LABEL_STYLES, className)}
    {...props}
  />
);

const DropdownMenuSeparator = ({
  className,
  ...props
}: ComponentProps<typeof Separator>): ReactElement => (
  <Separator
    data-slot="dropdown-menu-separator"
    className={cn(styles.DROPDOWN_MENU_SEPARATOR_STYLES, className)}
    {...props}
  />
);

const DropdownMenuShortcut = ({ className, ...props }: ComponentProps<'span'>): ReactElement => (
  <span
    data-slot="dropdown-menu-shortcut"
    className={cn(styles.DROPDOWN_MENU_SHORTCUT_STYLES, className)}
    {...props}
  />
);

const DropdownMenuSub = ({ ...props }: ComponentProps<typeof Sub>): ReactElement => (
  <Sub data-slot="dropdown-menu-sub" {...props} />
);

const DropdownMenuSubTrigger = ({
  className,
  inset,
  children,
  ...props
}: ComponentProps<typeof SubTrigger> & {
  inset?: boolean;
}): ReactElement => (
  <SubTrigger
    data-slot="dropdown-menu-sub-trigger"
    data-inset={inset}
    className={cn(styles.DROPDOWN_MENU_SUB_TRIGGER_STYLES, className)}
    {...props}
  >
    {children}
    <ChevronRightIcon className="ml-auto size-4" />
  </SubTrigger>
);

const DropdownMenuSubContent = ({
  className,
  ...props
}: ComponentProps<typeof SubContent>): ReactElement => (
  <SubContent
    data-slot="dropdown-menu-sub-content"
    className={cn(styles.DROPDOWN_MENU_SUB_CONTENT_STYLES, className)}
    {...props}
  />
);

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
